import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Date formats that dayjs can parse.
 */
const INPUT_FORMATS = [
  // ISO (dayjs detects it without format)
  // Formats without AM/PM
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY/M/D H:mm',
  'M/D/YYYY H:mm',
  'M-D-YYYY H:mm',
  'M-D-YY H:mm',
  // With AM/PM
  'M/D/YYYY h:mm A',
  'M-D-YYYY h:mm A',
  'M-D-YY h:mm A',
  'MM/DD/YYYY h:mm A',
  'MM-D-YYYY h:mm A',
  'YYYY-MM-DD h:mm A',
  'YYYY-MM-DD hh:mma',
  'YYYY-MM-DD h:mma',
];

function normalizeAmPm(s) {
  return String(s)
    .trim()
    .replace(/\s*(am|pm)\s*$/i, m => ' ' + m.trim().toUpperCase());
}

/**
 * deriveRange(date, view, timezone, weekStart)
 * Returns { start: Date, end: Date }
 * weekStart: 0..6 (0=Sunday). If undefined, default to 1 (Monday).
 */
export function deriveRange(date, view, timezone, weekStart) {
  const d = dayjs(date);
  const ws = typeof weekStart === 'number' ? weekStart : 1;

  if (view === 'day') {
    const start = d.startOf('day').toDate();
    const end = d.endOf('day').toDate();

    return { start, end };
  }

  if (view === 'week') {
    // Normalize to chosen week start
    const currentDow = d.day();
    const deltaToStart = (currentDow - ws + 7) % 7;
    const weekStartDate = d.subtract(deltaToStart, 'day').startOf('day');
    const start = weekStartDate.toDate();
    const end = weekStartDate
      .add(6, 'day')
      .endOf('day')
      .toDate();

    return { start, end };
  }

  if (view === 'month') {
    const start = d
      .startOf('month')
      .startOf('day')
      .toDate();
    const end = d
      .endOf('month')
      .endOf('day')
      .toDate();

    return { start, end };
  }

  if (view === 'quarter') {
    const start = d
      .startOf('quarter')
      .startOf('day')
      .toDate();
    const end = d
      .endOf('quarter')
      .endOf('day')
      .toDate();

    return { start, end };
  }

  if (view === 'year') {
    const start = d
      .startOf('year')
      .startOf('day')
      .toDate();
    const end = d
      .endOf('year')
      .endOf('day')
      .toDate();

    return { start, end };
  }

  // default to week
  return deriveRange(date, 'week', timezone, ws);
}

/**
 * Parse strictly trying several formats and normalize to tz.
 * @param {string|Date} input  Input date/time (flexible)
 * @param {string} tz  IANA TZ, e.g. 'America/Puerto_Rico'
 * @returns {dayjs.Dayjs|null} Valid instance or null if not parsed
 */
export function parseToDayjs(input, tz = 'America/Puerto_Rico') {
  if (!input) return null;
  const s = typeof input === 'string' ? normalizeAmPm(input) : input;
  // 1) If already Date or ISO valid, try directly
  let d = dayjs(s);
  if (d.isValid()) return tz ? d.tz(tz) : d;

  // 2) Try each format in strict mode
  for (const fmt of INPUT_FORMATS) {
    d = dayjs.tz(s, fmt, tz, true); // true = strict
    if (d.isValid()) return d;
  }

  return null;
}

/** Format util RBS typically uses: "YYYY-MM-DD" for date, and "YYYY-MM-DD HH:mm:ss" for datetimes.
 * @param {string|Date} date
 * @returns {string} 'YYYY-MM-DD'
 */
export function formatDateYYYYMMDD(date) {
  return dayjs(date).format('YYYY-MM-DD');
}

/** Ensure consistent string timestamps for events.
 * @param {string|Date} dt
 * @param {string} tz
 * @returns {string} 'YYYY-MM-DD HH:mm:ss'
 */
export function ensureDateTimeString(dt, tz = 'America/Puerto_Rico') {
  const d = parseToDayjs(dt, tz);

  if (!d) {
    console.warn(`Invalid date/time: "${dt}"`);
  }

  return d.format('YYYY-MM-DD HH:mm:ss');
}

export const eventWithinRange = (evt, range) => {
  const rStart = dayjs.utc(range.start);
  const rEnd = dayjs.utc(range.end);
  const eStart = dayjs.utc(evt.start, 'YYYY-MM-DD HH:mm');
  const eEnd = dayjs.utc(evt.end, 'YYYY-MM-DD HH:mm');
  return eStart.isSameOrAfter(rStart) && eEnd.isSameOrBefore(rEnd);
};


/**
 * Returns a function that, given a signature string, returns true if called twice
 * with the same signature within thresholdMs, otherwise false. Resets after a hit.
 */
export function createDoubleClickDetector(thresholdMs = 300) {
  const state = { t: 0, sig: '' };

  return function doubleClick(signature) {
    const now = Date.now();

    if (state.sig === signature && now - state.t < thresholdMs) {
      state.t = 0;
      state.sig = '';

      return true;
    }

    state.t = now;
    state.sig = signature;

    return false;
  };
}
